import type { AttributionData } from "@fluid-app/api-client";
import type {
  AttributionManager,
  AttributionOverride,
} from "@fluid-app/fairshare";

import type { FluidCore } from "./FluidCore";
import { PageChangeDetector } from "./PageChangeDetector";

// No need for a separate config interface - just use AttributionData directly

/**
 * Attribution integration layer that coordinates page change detection
 * with attribution refresh and manages hard-coded attribution
 */
export class AttributionIntegration {
  private pageChangeDetector: PageChangeDetector;
  private fluidCore: FluidCore;
  private attributionManager: AttributionManager | null = null;
  private isInitialized = false;

  constructor(fluidCore: FluidCore) {
    this.fluidCore = fluidCore;
    this.pageChangeDetector = new PageChangeDetector();
  }

  /**
   * Initialize the attribution integration system
   * @param attributionOverride Optional attribution override data
   */
  public async initialize(
    attributionOverride?: AttributionData,
  ): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize attribution manager
      const { AttributionManager } = await import("@fluid-app/fairshare");
      // We'll need to get the storage manager from FluidCore
      this.attributionManager = new AttributionManager(
        this.fluidCore.getStorageManager(),
      );

      // Set up attribution override if provided
      if (attributionOverride) {
        this.setAttributionOverride(attributionOverride);
      }

      // Set up page change detection
      this.setupPageChangeDetection();

      // Perform initial attribution refresh for current page
      await this.refreshAttributionForCurrentPage();

      this.isInitialized = true;
      console.log("AttributionIntegration: Initialized successfully");
    } catch (error) {
      console.error("Error initializing AttributionIntegration:", error);
      throw error;
    }
  }

  /**
   * Set attribution override that overrides server-calculated attribution
   */
  public setAttributionOverride(attribution: AttributionData): void {
    if (!this.attributionManager) {
      console.warn(
        "AttributionManager not initialized, cannot set attribution override",
      );
      return;
    }

    const attributionOverride: AttributionOverride = {
      attribution: attribution,
    };

    this.attributionManager.setAttributionOverride(attributionOverride);
    console.log(
      "AttributionIntegration: Set attribution override:",
      attributionOverride,
    );
  }

  /**
   * Clear attribution override, falling back to server-calculated attribution
   */
  public clearAttributionOverride(): void {
    if (!this.attributionManager) {
      console.warn(
        "AttributionManager not initialized, cannot clear attribution override",
      );
      return;
    }

    this.attributionManager.clearAttributionOverride();
    console.log("AttributionIntegration: Cleared attribution override");
  }

  /**
   * Get effective attribution data respecting priority (hard-coded > server > fallback)
   */
  public getEffectiveAttribution(url?: string, useCachedAttribution?: boolean) {
    if (!this.attributionManager) {
      return null;
    }

    return this.attributionManager.getEffectiveAttribution({
      url,
      useCachedAttribution: useCachedAttribution ?? false,
    });
  }

  /**
   * Get attribution source information for debugging
   */
  public getAttributionSource():
    | "attribution-override"
    | "server"
    | "none"
    | "cookie" {
    if (!this.attributionManager) {
      return "none";
    }

    return this.attributionManager.getAttributionSource();
  }

  /**
   * Set up page change detection and attribution refresh
   */
  private setupPageChangeDetection(): void {
    // Add callback for URL changes
    this.pageChangeDetector.addCallback((url: string) => {
      console.log("AttributionIntegration: Page changed to:", url);
      void this.handlePageChange(url);
    });

    // Start listening for page changes
    this.pageChangeDetector.startListening();
  }

  /**
   * Handle page change events
   */
  private async handlePageChange(url: string): Promise<void> {
    try {
      // Check if we need to refresh attribution for this URL
      if (this.attributionManager?.needsRefresh(url)) {
        await this.refreshAttributionForPage(url);
      }
    } catch (error) {
      console.error("Error handling page change:", error);
    }
  }

  /**
   * Refresh attribution for the current page
   */
  public async refreshAttributionForCurrentPage(): Promise<void> {
    const currentUrl =
      typeof window !== "undefined" ? window.location.href : "";
    await this.refreshAttributionForPage(currentUrl);
  }

  /**
   * Refresh attribution data for a specific page
   */
  private async refreshAttributionForPage(url: string): Promise<void> {
    try {
      console.log("AttributionIntegration: Refreshing attribution for:", url);
      // Fetch fresh settings for this page using the new fetchSettings
      const settings = await this.fluidCore.fetchSettings(url);
      if (settings) {
        // Extract attribution data from settings
        const attributionData =
          this.fluidCore.extractAttributionFromSettings(settings);
        // Store attribution data in server attribution manager
        if (this.attributionManager) {
          this.attributionManager.storeServerAttribution(url, attributionData);
        }
        console.log(
          "AttributionIntegration: Stored attribution for URL:",
          url,
          attributionData,
        );
      } else {
        console.log(
          "AttributionIntegration: No settings received for URL:",
          url,
        );
      }
    } catch (error) {
      console.error("Error refreshing attribution for page:", error);
      // Don't throw error to prevent breaking app functionality
      // Log error and continue gracefully
    }
  }

  /**
   * Stop page change detection
   */
  public destroy(): void {
    this.pageChangeDetector.stopListening();
    this.isInitialized = false;
    console.log("AttributionIntegration: Destroyed");
  }

  /**
   * Get the current attribution override, or undefined if not set
   */
  public getAttributionOverride(): AttributionData | undefined {
    return this.attributionManager?.getAttributionOverride() ?? undefined;
  }

  /**
   * Store server attribution for a given URL
   */
  public storeServerAttribution(
    url: string,
    attribution: AttributionData,
  ): void {
    if (this.attributionManager) {
      this.attributionManager.storeServerAttribution(url, attribution);
    }
  }
}
